import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { defer, firstValueFrom, from, map, Observable, of } from "rxjs";
import { AppState } from "../../core";
import { activity_status, Path } from "../../core/models";
import {
    BuildingService,
    PathService,
    PathWaypointService,
    PointService,
    UserService
} from "../../core/services";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { FesBaseComponent } from "../../shared/components/fes-base.component";
import {
    TableViewQueryCriteria,
    TableViewQueryResponse
} from "../../shared/components/table-view/table-view-component-base";
import { Volunteer } from "../models/volunteer";
import { VolunteerService } from "../services/volunteer.service";
import StatusId = activity_status.Id;
import { CustomValidators } from "@narik/custom-validators";
import { VolunteerHandsetRec } from "../models/volunteer-handset";
import { NotEmptyValidator } from "../../shared/validators/not-empty.validator";

const NO_PATH_CHOICE = '-- No Path --';

@Component({
  selector: "app-volunteer-edit",
  templateUrl: "./volunteer-edit.component.html",
  styleUrls: ["../volunteer-list/volunteer-list.component.scss","./volunteer-edit.component.scss"],
})
export class VolunteerEditComponent
  extends FesBaseComponent
  implements OnInit, OnDestroy
{
  static showModal( modalService: BsModalService, volunteer: Volunteer, modalClass='modal-lg') {
    return modalService.show(VolunteerEditComponent, {
      ignoreBackdropClick: true,
      initialState: { volunteer },
      class: modalClass,
    });
  }

  // TODO Make volunteer type correct so no confusion about VolunteerEntity
  // volunteer is form data (VolunteerEntity with points and handset elements)
  // If data is passed from a VolunteerEntity without the points and handset,
  // all existing db points and handset data will be removed on save.
  @Input("volunteer") volunteer: Volunteer = null;
  public bProcessing = false;
  public errorMsg: string = "";
  volunteerForm: FormGroup;
  pathIds: any[];
  descriptions: any[] = [];

  buildingInfoArray: { id: string; name: string }[] = [];
  routeInfoArray: { id: string; market: string }[] = [];
  pointIds: any[];
  accessiblePointIds: any[] = [];
  statusByPointId: { [pointId: string]: StatusId } = {};
  canToggleByStatusId = {
    [StatusId.NOT_USING]: true,
    [StatusId.NOT_STARTED]: true,
  };
  modifiedBy$ : Observable<string>;

  @Input() set buildingNamesById(namesById: { [key: string]: string }) {
    this.buildingInfoArray = Object.entries(namesById).map(([id, name]) => ({
      id,
      name,
    }));
    this.buildingInfoArray.sort((a, b) => (a?.id > b?.id ? 1 : -1));
    if (this.volunteer) {
      this.volunteerForm.controls["buildingId"].setValue(
        this.volunteer?.points[0]?.buildingId || this.volunteer.volunteerBuildingId
      );
      this.volunteerForm.controls["volunteerBuildingId"].setValue(
        this.volunteer.volunteerBuildingId
      );
      this.onBuildingIdChanged();
    }
  }

  @Input() set routeMarketsById(marketsById: { [key: string]: string }) {
    this.routeInfoArray = Object.entries(marketsById).map(([id, market]) => ({
      id,
      market,
    }));
    this.routeInfoArray.sort((a, b) => (a?.id > b?.id ? 1 : -1));
  }

  accessiblePointsModified = false;
  //TBD - Should "Save" be enabled if accessible points array is empty???

  constructor(
    appState: AppState,
    protected editModal: BsModalRef,
    private formBuilder: FormBuilder,
    private volunteerService: VolunteerService,
    private pathService: PathService,
    private pathWayPointService: PathWaypointService,
    private buildingService: BuildingService,
    private pointService: PointService,
    private bsModalService: BsModalService,
    private userService: UserService,
  ) {
    super(appState);
  }

  ngOnInit(): void {
    this.createForm();
    if (this.volunteer) {
      this.volunteerForm.patchValue(this.volunteer);
      this.volunteer.points?.forEach((volPoint) => {
        this.accessiblePointIds.push(volPoint.pointId);
      });
    } else {
      this.volunteerForm.controls["password"].addValidators(
        Validators.required
      );
    }
    this.modifiedBy$ = defer(()=>{
      // support old getVolunteers SQL instead of real TypeORM entity
      let name = this.volunteer?.['modifiedByName'];
      if ( name || !this.volunteer?.modifiedBy) {
        return of(name);
      } else {
        return from(this.userService.getById(this.volunteer?.modifiedBy))
          .pipe( map( userDoc=> userDoc ? `${userDoc.firstName} ${userDoc.lastName}` : ''));
      }
    });
    this.initBuildingCommonNames().catch(e=>console.error);
    this.patchValue();
  }

  toggleNotUsing(pointId: string) {
    let found = false;
    this.accessiblePointsModified = true; //TODO revisit logic for deciding if points
    this.pointIds.forEach((single_point) => {
      if (single_point == pointId) {
        found = true;
        if (!this.accessiblePointIds.includes(single_point))
          this.accessiblePointIds.push(single_point);
        else {
          this.accessiblePointIds.splice(
            this.accessiblePointIds.indexOf(single_point),
            1
          );
        }
      }
    });
    if (!found) {
      this.accessiblePointIds.splice(
        this.accessiblePointIds.indexOf(pointId),
        1
      );
    }
    const docPtStatusId =
      this.pointIds?.find((pt) => pt == pointId)?.statusId || StatusId.COMPLETE;
    this.statusByPointId[pointId] =
      this.statusByPointId[pointId] != StatusId.NOT_STARTED
        ? StatusId.NOT_STARTED
        : docPtStatusId == StatusId.NOT_STARTED
        ? StatusId.COMPLETE
        : docPtStatusId;
    this.volunteerForm.markAsDirty();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  async onSave() {
    this.errorMsg = "";
    this.volunteerForm.updateValueAndValidity();
    for( const [name,control] of Object.entries(this.volunteerForm.controls)) {
      console.log('name', name, 'control valid', control.valid)
      if (control.invalid) {
        control.markAsDirty();
        this.appState.notifyError(name +" is invalid",AppState,5);
        return;
      }
    }

    this.bProcessing = true;

    try {
      if (!this.volunteer || !this.volunteer.id) {
        const createResult = await this.volunteerService.create({
          ...this.volunteerForm.value,
        },this.accessiblePointIds);
        //console.log('volunteer created', createResult);
      } else {
        //TBD - modifiedBy resolved on server based on login JWT?
        this.volunteerForm.value.modifiedBy = this.appState.getUser()._id;
        const updateResult = await this.volunteerService.update(this.volunteerForm.value, this.accessiblePointIds);
        //console.log('volunteer updated', updateResult);
      }
      this.editModal.hide();
    } catch (e: any) {
      console.error(e);
      this.errorMsg = this.volunteerService.extractErrorMsg(e);
    } finally {
      this.bProcessing = false;
    }
  }

  async onBuildingIdChanged() {
    await this.findPathIdsForBuilding(this.volunteerForm.value.buildingId);
  }

  async onVolunteerBuildingIdChange() {
    const volBuildingId = this.volunteerForm.value.volunteerBuildingId;
    if ( volBuildingId && !this.volunteerForm.value.buildingId) {
      this.volunteerForm.controls["buildingId"].setValue(volBuildingId);
      await this.onBuildingIdChanged();
    }
  }

  @Output('handsetChanged') handsetChanged = new EventEmitter<VolunteerHandsetRec>();

  async resetDeviceId() {
    let bConfirmed = false;
    let initialState = {
      onYes: () => {
        bConfirmed = true;
      },
      question: `Reset handset device ID (new value will be set during mobile app login)?`,
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    const modalRef = this.bsModalService.show(ConfirmModalComponent, { initialState });
    await firstValueFrom(modalRef.onHidden);
    if ( bConfirmed ) {
      this.errorMsg = '';
      try {
        const updatedHandset = await this.volunteerService.patchHandset({
          id: this.volunteer['handsetId'],
          deviceId: null
        });
        this.volunteer['deviceId'] = updatedHandset.deviceId;
        this.handsetChanged.emit(updatedHandset);
      } catch (e:any){
        console.error(e);
        const msg = this.pathWayPointService.extractErrorMsg(e);
        this.errorMsg = msg;
      }
    }
  }

  async findPathIdsForBuilding(buildingId) {
    this.pathIds = [];
    this.pointIds = [];

    if (!buildingId) {
      return;
    }

    try {
      const queryCriteria: TableViewQueryCriteria = {
        filters: [{ mode: "=", key: "buildingid", value: buildingId }],
        sorts: [{ key: "id", order: "ASC" }],
      };
      const result: any = await firstValueFrom(this.pathService.http
        .get(`${this.pathService.urlPath}/find`, {
          params: { criteria: JSON.stringify(queryCriteria) },
        }));
      const resp: TableViewQueryResponse<Path> = result.data;
      this.pathIds = [ NO_PATH_CHOICE, ...resp.rows.map((row) => row.id)];
      if (this.volunteerForm.controls) {
        //this.volunteerForm.controls["pathId"].setValue(
        //  this.volunteer?.points[0]?.pathID
        //);
        this.onPathIdChanged(this.volunteerForm.value.pathId);
      }
    } catch (error) {
      console.error(error);
      this.pathWayPointService.publishError(error, this);
    }
  }

  cancelled = false;
  onCancel() {
    if (this.volunteerForm.dirty) {
      let initialState = {
        onYes: () => {
          this.cancelled = true;
          this.editModal.hide();
        },
        question: `Data entered in the fields will be lost.Are you sure you wish to cancel?`,
        focusOnNo: true,
        yesStyle: "btn btn-danger",
      };
      this.bsModalService.show(ConfirmModalComponent, { initialState });
    } else {
      this.cancelled = true;
      this.editModal.hide();
    }
  }

  private createForm() {
    this.volunteerForm = this.formBuilder.group({
      id: [this.volunteer?.id, [NotEmptyValidator.validate]],
      name: ["", NotEmptyValidator.validate],
      email: ["", [Validators.required, Validators.email]],
      city: ["", NotEmptyValidator.validate],
      state: [""],
      volunteerBuildingId: ["", Validators.required],
      chestHeight:[null, CustomValidators.number],
      password: [""],
      buildingId: [""],
      debug: [false],
      notes: [""],
      pathId: [""],//, Validators.required],
      modifiedBy: [this.appState.getUser()._id],
      handsetId: [""],
      number: [""],
      imei: [""],
      bt_mac: [""],
      carrier: [""],
      make: [""],
      model: [""],
      os: [""],
      status: ["pending"]
    });
  }

  private async initBuildingCommonNames() {
    const allBuildings = await this.buildingService.restClient.findRows({
      respEntityFields: ['id','commonName'],
      sorts: [{key:'id', order:'ASC'}]
    });
    //const allBuildings = await this.buildingService.rxCollection.find().exec();
    const buildingNamesById = {};
    allBuildings.forEach(building => {
      if ( !building ) {
        console.warn(`Invalid state.  Null building from findRows()`);
      } else {
        buildingNamesById[building.id] = building.commonName;
      }
    });
    this.buildingNamesById = buildingNamesById;
  }

  onPathIdChanged(pathId: string) {
    if ( pathId ) {
      if ( pathId === NO_PATH_CHOICE ) {
        this.initPointsForNoPathQuery();
      } else {
        this.initPointsForPathQuery(pathId);
      }
    }
  }

  pathPtsInitProcessing = false;
  async initPointsForPathQuery(pathId) {
    this.pointIds = [];
    if (!pathId) {
      return;
    }
    try {
      this.pathPtsInitProcessing = true;
      this.errorMsg = '';
      const waypointsResp = await this.pathWayPointService.restClient.find({
        filters: [
          {key:'pathId', value: pathId, mode: '='},
          {key:'type', value:'^(HA|EX)$', mode: '~*'},
        ]
      });
      const waypoints = waypointsResp.rows;
      // const waypoints = await this.pathWaypointService.rxCollection
      //   .find({
      //     selector: { pathId, type: { $regex: /^(HA|EX)$/i } }
      //   }).exec();
      this.pointIds = waypoints.map((wp) => wp.pointId).filter(ptId=>ptId&&!ptId.toUpperCase().endsWith('REF'));
      const ptIdsNeedingDescription = this.pointIds.filter(ptId=>this.descriptions[ptId]===undefined);
      if (ptIdsNeedingDescription.length > 0){
        const points = await this.pointService.restClient.getByIds(ptIdsNeedingDescription);
        points.filter(pt=>pt&&pt.floorAndDescript).forEach( pt =>
          this.descriptions[pt._id] = pt.floorAndDescript
        );
      }
      this.statusByPointId = this.pointIds.reduce((sById, ptId) => {
        sById[ptId] = StatusId.NOT_STARTED;
        return sById;
      }, {});
      this.patchValue();
    } catch ( e: any ) {
      this.errorMsg = this.pathWayPointService.restClient.extractErrorMsg(e);
      this.appState.notifyWarning(this.errorMsg, this, 3);
    } finally {
      this.pathPtsInitProcessing = false;
    }

  }

  async initPointsForNoPathQuery() {
    const buildingId = this.volunteerForm.value.buildingId;
    if ( !buildingId ) {
      return;
    }
    try {
      const points = await this.pointService.findPathlessByBuildingId(buildingId);
      this.pointIds = points?.map(point=>point._id).filter(ptId=>ptId&&!ptId.toUpperCase().endsWith('REF'));
      this.statusByPointId = this.pointIds.reduce((sById, ptId) => {
        sById[ptId] = StatusId.NOT_STARTED;
        return sById;
      }, {});
      this.patchValue();
    } catch ( error: any ) {
      const msg = this.pointService.extractErrorMsg(error);
      this.pointService.publishError(error,this);
      this.appState.notifyError(msg,this,3);
    }
  }

  private patchValue() {
    setTimeout(() => {
      this.accessiblePointIds.forEach((single_point) => {
        this.statusByPointId[single_point] = StatusId.COMPLETE;
      });
    });
  }

  onInputChange(event) {
    let input = event.target.value.replace(/\D/g,'');

    // Trim the remaining input to ten characters, to preserve phone number format
    input = input.substring(0,10);

    // Based upon the length of the string, we add formatting as necessary
    var size = input.length;
    if(size == 0){
      input = input;
    }else if(size < 4){
      input = '('+input;
    }else if(size < 7){
      input = '('+input.substring(0,3)+')'+input.substring(3,6);
    }else {
      input = '(' + input.substring(0, 3) + ')' + input.substring(3, 6) + '-' + input.substring(6, 10);

    }
    this.volunteerForm.controls['number'].setValue(input);
  }

}
